import { Button, Form, Input, InputNumber, Select, Space, Spin, Switch, Checkbox } from 'antd'
import React, { useEffect, useMemo, useState } from 'react'
import { connect } from 'react-redux'
import TextArea from 'antd/es/input/TextArea'
import { ReloadOutlined } from '@ant-design/icons'
import { actions } from '../../../../../store/reducers/assignment.reducer'
import { useParams } from 'react-router'
import CriteriaFields from '../../../../../components/common/criteria/CriteriaFields'
import { v4 } from 'uuid'
import { ASSIGNMENT_IS_TOO_BIG_FOR_PROJECTION } from '../../../../../constants/response-messages'
import { GROUPING_CAP_TYPES } from '../../../../../constants/grouping-cap-types'
import useProjectionCostEstimation from '../../../../../hooks/useProjectionCostEstimation'
import { getLlmVersionName } from '../../../../../constants/llm-versions'
import TextEditor from '../../../../../components/common/text-editor/TextEditor'
import PatternIcon from '../../../../../components/common/PatternIcon'
import { getPatternWarningLevelColor, getPatternWarningLevelName, PatternWarningLevels } from '../../../../../constants/pattern-warning-levels'
import { colorOptions } from '../../../../../utils/antd.helper'
import ConceptualLabelSelect from '../../../../../pages/resources/conceptual-labels/select/ConceptualLabelSelect'
import { useAutoFocusFirstInput } from '../../../../../../hooks/useAutoFocusFirstInput'

const ReviewPatternStep = ({
    setFocusedCriterion,
    projectCriteria,
    createSemanticPattern,
    semanticPatternDetails,
    regenerateAndProjectCriteria,
    setColorIdOfCreatedSemanticPatternDetails,
}) => {
    const { token, layerId } = useParams()

    const [form] = Form.useForm()
    useAutoFocusFirstInput()
    const [isWaitingForCreateSemanticPattern, setIsWaitingForCreateSemanticPattern] = useState(false)
    const [isWaitingForRegenerateAndProjectCriteria, setIsWaitingForRegenerateAndProjectCriteria] = useState(false)
    const [criteriaFields, setCriteriaFields] = useState([])

    useEffect(() => {
        form.setFieldsValue({
            name: semanticPatternDetails?.name,
            hitFeedback: semanticPatternDetails?.feedback,
            missFeedback: '',
            showHitReasoning: false,
            showMissReasoning: false,
            description: semanticPatternDetails?.description,
            grade: 0,
            colorId: semanticPatternDetails?.colorId,
            groupingCap: GROUPING_CAP_TYPES.ONE.value,
            regroup: true,
            warningLevel: PatternWarningLevels.NEUTRAL,
            conceptualLabels: semanticPatternDetails?.conceptual_labels,
        })
        setCriteriaFields(
            semanticPatternDetails?.projection.map(projection => ({
                id: v4(),
                value: projection.criterion,
                isMet: projection.is_met,
                reasoning: projection.reasoning,
                matchConfidenceInPercent: projection.match_confidence * 100,
            })),
        )
    }, [])

    const arrayOfCriteria = useMemo(() => [criteriaFields.map(field => field.value)], [criteriaFields])
    const projectionCostDetails = useProjectionCostEstimation(arrayOfCriteria)
    const isAssignmentTooBigForProjection = projectionCostDetails === ASSIGNMENT_IS_TOO_BIG_FOR_PROJECTION
    const isWaitingForEstimateProjectionCost = projectionCostDetails === '...'

    const handleSubmitClick = async () => {
        setIsWaitingForCreateSemanticPattern(true)
        await createSemanticPattern(
            token,
            layerId,
            form.getFieldValue('name')?.trim(),
            null,
            form.getFieldValue('description')?.trim(),
            criteriaFields.map(field => field.value?.trim()),
            form.getFieldValue('grade'),
            form.getFieldValue('colorId'),
            form.getFieldValue('groupingCap'),
            form.getFieldValue('regroup'),
            form.getFieldValue('hitFeedback')?.trim(),
            form.getFieldValue('missFeedback')?.trim(),
            form.getFieldValue('showHitReasoning'),
            form.getFieldValue('showMissReasoning'),
            form.getFieldValue('warningLevel'),
            form.getFieldValue('conceptualLabels'),
        )
        setIsWaitingForCreateSemanticPattern(false)
    }

    const handleRegenerateAndProjectCriteria = async () => {
        setIsWaitingForRegenerateAndProjectCriteria(true)
        const { name, feedback, projection } = await regenerateAndProjectCriteria(
            token,
            layerId,
            form.getFieldValue('description'),
        )
        setIsWaitingForRegenerateAndProjectCriteria(false)
        setCriteriaFields(
            projection.map(projection => ({
                id: v4(),
                value: projection.criterion,
                isMet: projection.is_met,
                reasoning: projection.reasoning,
                matchConfidenceInPercent: projection.match_confidence * 100,
            })),
        )
        form.setFieldsValue({ name, hitFeedback: feedback })
    }

    const handleColorSelect = value => {
        setColorIdOfCreatedSemanticPatternDetails(value)
    }

    const descriptionLabel = (
        <>
            Description{' '}
            <Button
                type='text'
                shape='circle'
                title='Regenerate and project criteria'
                loading={isWaitingForRegenerateAndProjectCriteria}
                icon={<ReloadOutlined />}
                onClick={() => handleRegenerateAndProjectCriteria()}
            />
        </>
    )

    const groupingCapOptions = Object.keys(GROUPING_CAP_TYPES).map(key => ({
        label: GROUPING_CAP_TYPES[key].label,
        value: GROUPING_CAP_TYPES[key].value,
    }))

    const warningLevelOptions = Object.values(PatternWarningLevels).map(level => ({
        label: (
            <Space size={8}>
                <PatternIcon id={level} color={getPatternWarningLevelColor(level)} showId={false} />{' '}
                {getPatternWarningLevelName(level)}
            </Space>
        ),
        value: level,
    }))

    return (
        <Form
            name='review-pattern'
            autoComplete='off'
            form={form}
            onFinish={handleSubmitClick}
            requiredMark={false}
            layout='vertical'
        >
            <Form.Item
                label='Name'
                name='name'
                rules={[{ required: true, message: 'Please, enter name for this pattern' }]}
            >
                <Input placeholder='Enter name for this pattern' />
            </Form.Item>
            <Form.Item
                name='conceptualLabels'
                label='Conceptual labels'
                initialValue={semanticPatternDetails?.conceptual_labels}
                tooltip='Conceptual labels identify the key principles to be demonstrated in student work. Select labels that connect specific work patterns to broader educational concepts the student demonstrates or needs to develop.'
            >
                <ConceptualLabelSelect />
            </Form.Item>
            <Form.Item
                label={descriptionLabel}
                name='description'
                rules={[{ required: true, message: 'Please, enter description for this pattern' }]}
            >
                <TextArea rows={4} placeholder='Enter description for this pattern' />
            </Form.Item>
            <Form.Item 
                name='hitFeedback'
                rules={[{ required: true, message: 'Please, input hit feedback' }]}
                extra={
                    <Form.Item name='showHitReasoning' valuePropName='checked' noStyle>
                        <Checkbox>Show reasoning to students on hit</Checkbox>
                    </Form.Item>
                }
            >
                <TextEditor label='Hit Feedback' placeholder='Enter hit feedback for this pattern' />
            </Form.Item>
            <Form.Item 
                name='missFeedback'
                extra={
                    <Form.Item name='showMissReasoning' valuePropName='checked' noStyle>
                        <Checkbox>Show reasoning to students on miss</Checkbox>
                    </Form.Item>
                }
            >
                <TextEditor label='Miss Feedback' placeholder='Enter miss feedback for this pattern' />
            </Form.Item>
            <Spin spinning={isWaitingForRegenerateAndProjectCriteria} tip='Regenerating criteria...'>
                <CriteriaFields
                    fields={criteriaFields}
                    setFields={setCriteriaFields}
                    regenerateReasoning={criteria => projectCriteria(token, criteria)}
                    setFocusedCriterion={setFocusedCriterion}
                />
            </Spin>
            <Space className='d-flex-space-between'>
                <Form.Item
                    label='Color'
                    name='colorId'
                    rules={[{ required: true, message: 'Please, select color for this pattern' }]}
                >
                    <Select 
                        options={colorOptions} 
                        onSelect={handleColorSelect} 
                        popupMatchSelectWidth={false}
                        getPopupContainer={triggerNode => triggerNode.parentNode}
                        showSearch
                        optionFilterProp='label'
                    />
                </Form.Item>
                <Form.Item label='Grade' name='grade' rules={[{ type: 'number', message: 'Please, input a number' }]}>
                    <InputNumber />
                </Form.Item>
                <Form.Item name='regroup' label='Regroup' valuePropName='checked' initialValue={true}>
                    <Switch aria-label='regroup' />
                </Form.Item>
            </Space>
            <Form.Item label='Grouping cap' name='groupingCap'>
                <Select 
                    options={groupingCapOptions}
                    popupMatchSelectWidth={false}
                    getPopupContainer={triggerNode => triggerNode.parentNode}
                    showSearch
                    optionFilterProp='label'
                />
            </Form.Item>
            <Form.Item
                label='Warning level'
                name='warningLevel'
                rules={[{ required: true, message: 'Please, select warning level' }]}
            >
                <Select 
                    options={warningLevelOptions}
                    popupMatchSelectWidth={false}
                    getPopupContainer={triggerNode => triggerNode.parentNode}
                    showSearch
                    optionFilterProp='label'
                />
            </Form.Item>
            <div className='mb-16'>
                LLM version: {<code>{getLlmVersionName(semanticPatternDetails?.llmVersion)}</code> || '...'}
            </div>
            <Form.Item>
                <Button
                    type='primary'
                    htmlType='submit'
                    block
                    loading={isWaitingForCreateSemanticPattern || isWaitingForEstimateProjectionCost}
                    disabled={isAssignmentTooBigForProjection}
                >
                    Preview {projectionCostDetails}
                </Button>
            </Form.Item>
        </Form>
    )
}

const mapState = ({ assignment }) => {
    return {
        semanticPatternDetails: assignment.semanticPatternDetails,
    }
}

const mapDispatch = dispatch => {
    return {
        createSemanticPattern(
            token,
            layerId,
            name,
            feedback,
            description,
            criteria,
            grade,
            colorId,
            groupingCap,
            regroup,
            hitFeedback,
            missFeedback,
            showHitReasoning,
            showMissReasoning,
            warningLevel,
            conceptualLabels,
        ) {
            return dispatch(
                actions.createSemanticPattern(
                    token,
                    layerId,
                    name,
                    feedback,
                    description,
                    criteria,
                    grade,
                    colorId,
                    groupingCap,
                    regroup,
                    hitFeedback,
                    missFeedback,
                    showHitReasoning,
                    showMissReasoning,
                    warningLevel,
                    conceptualLabels,
                ),
            )
        },
        projectCriteria(token, criteria) {
            return dispatch(actions.projectCriteria(token, criteria))
        },
        regenerateAndProjectCriteria(token, layerId, description) {
            return dispatch(actions.regenerateAndProjectCriteria(token, layerId, description))
        },
        setColorIdOfCreatedSemanticPatternDetails(value) {
            return dispatch(actions.setColorIdOfCreatedSemanticPatternDetails(value))
        },
    }
}

export default connect(mapState, mapDispatch)(ReviewPatternStep)
